import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Wallet, AlertCircle, Cake, Building2, TrendingUp, Briefcase, UserPlus, LogOut, FileWarning, CheckSquare, PartyPopper, ScanFace, Award, ArrowRight, Send } from 'lucide-react';
import { formatINR } from '../../lib/format';
import { apiGet, apiPost } from '../../lib/api';
import SuspiciousPunchesTile from '../../components/hr/SuspiciousPunchesTile';

export default function HRDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ext, setExt] = useState(null);
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [celebrationView, setCelebrationView] = useState('anniversaries'); // 'anniversaries' | 'birthdays'

  useEffect(() => {
    Promise.all([
      apiGet('/hr/dashboard').catch(() => null),
      apiGet('/hr/dashboard-ext').catch(() => null),
    ]).then(([d, e]) => { setData(d); setExt(e); setLoading(false); });
    apiGet('/hr/training/dashboard').then(setTraining).catch(() => {});
  }, []);

  if (loading) return <div className="text-slate-500 py-10 text-center">Loading HR dashboard…</div>;
  if (!data) return <div className="text-slate-500 py-10 text-center">No data available.</div>;

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="hr-dashboard">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">HR Command Center</h1>
        <p className="text-sm text-slate-600 mt-1">Workforce master · Lifecycle · Attendance · Payroll · Compliance.</p>
      </div>

      {/* Extended KPIs (Phase 1+2) */}
      {ext && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hr-ext-kpis">
          <KPI icon={UserPlus} label="New Joiners (60d)" value={ext.new_joiners_60d} sub="In onboarding" testId="kpi-new-joiners" />
          <KPI icon={CheckSquare} label="Pending Approvals" value={ext.pending_approvals?.total || 0} sub={`Leaves ${ext.pending_approvals?.leaves || 0} · Reg ${ext.pending_approvals?.regularizations || 0} · OT ${ext.pending_approvals?.overtime || 0}`} tone={ext.pending_approvals?.total ? 'amber' : 'default'} testId="kpi-pending-approvals" />
          <KPI icon={LogOut} label="Exits in Notice" value={ext.exits_in_notice} sub="In progress" tone={ext.exits_in_notice ? 'amber' : 'default'} testId="kpi-exits-notice" />
          <KPI icon={FileWarning} label="Doc Alerts" value={(ext.documents?.expired || 0) + (ext.documents?.expiring_soon || 0)} sub={`${ext.documents?.expired || 0} expired · ${ext.documents?.expiring_soon || 0} soon`} tone={(ext.documents?.expired || 0) > 0 ? 'red' : (ext.documents?.expiring_soon || 0) > 0 ? 'amber' : 'default'} testId="kpi-doc-alerts" />
        </div>
      )}

      {/* Core KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Headcount" value={data.headcount} sub={`${data.total_employees} total`} testId="kpi-headcount" />
        <KPI icon={Wallet} label="Monthly Cost" value={formatINR(data.monthly_cost)} sub={`Annual ${formatINR(data.annual_cost)}`} testId="kpi-monthly-cost" />
        <KPI icon={AlertCircle} label="Pending Leaves" value={data.pending_leaves} sub="Approval needed" tone={data.pending_leaves > 0 ? 'amber' : 'default'} testId="kpi-pending-leaves" />
        <KPI icon={TrendingUp} label="Attrition (12m)" value={data.attrition_12m} sub="Resignations" testId="kpi-attrition" />
      </div>

      {/* Face enrolment coverage */}
      {data.face_enrolment && <FaceEnrolmentCard fe={data.face_enrolment} />}

      {/* GPS suspicious punches */}
      <SuspiciousPunchesTile />

      {/* Training certification expiry */}
      {training && (training.expiring_count > 0 || training.expired_count > 0) && (
        <button onClick={() => navigate('/app/hr/training')} data-testid="training-expiry-card"
          className="w-full text-left">
          <Card className={`border-l-4 ${training.expired_count > 0 ? 'border-l-red-500' : 'border-l-amber-500'} hover:shadow-md transition-shadow`}>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Award size={22} className={training.expired_count > 0 ? 'text-red-500' : 'text-amber-500'} />
                <div>
                  <div className="font-display font-bold text-sm">Training Certifications Need Attention</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="font-bold text-amber-600">{training.expiring_count}</span> expiring (30d) ·
                    <span className="font-bold text-red-600"> {training.expired_count}</span> expired ·
                    <span className="text-slate-400"> {training.total_certified} total certified</span>
                  </div>
                </div>
              </div>
              <span className="text-xs text-[#1F7A6E] font-display font-bold uppercase tracking-wider flex items-center gap-1">Review <ArrowRight size={13} /></span>
            </CardContent>
          </Card>
        </button>
      )}

      {/* Upcoming birthdays / anniversaries */}
      {ext && (ext.upcoming_birthdays?.length > 0 || ext.upcoming_anniversaries?.length > 0) && (
        <Card data-testid="celebrations-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                {celebrationView === 'anniversaries' ? <PartyPopper size={14} /> : <Cake size={14} />}
                {celebrationView === 'anniversaries' ? 'Work Anniversaries' : 'Upcoming Birthdays'} (next 7 days)
              </CardTitle>
              <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden text-[11px] font-display font-bold uppercase tracking-wider" data-testid="celebrations-toggle">
                <button
                  type="button"
                  onClick={() => setCelebrationView('anniversaries')}
                  className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${celebrationView === 'anniversaries' ? 'bg-[#1F7A6E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="toggle-anniversaries"
                >
                  <PartyPopper size={11} /> Anniversaries
                  <span className={`ml-1 px-1.5 py-0.5 rounded-sm ${celebrationView === 'anniversaries' ? 'bg-white/25' : 'bg-slate-100 text-slate-600'}`}>
                    {ext.upcoming_anniversaries?.length || 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setCelebrationView('birthdays')}
                  className={`px-3 py-1.5 flex items-center gap-1.5 border-l border-slate-300 transition-colors ${celebrationView === 'birthdays' ? 'bg-pink-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="toggle-birthdays"
                >
                  <Cake size={11} /> Birthdays
                  <span className={`ml-1 px-1.5 py-0.5 rounded-sm ${celebrationView === 'birthdays' ? 'bg-white/25' : 'bg-slate-100 text-slate-600'}`}>
                    {ext.upcoming_birthdays?.length || 0}
                  </span>
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {celebrationView === 'anniversaries' ? (
              ext.upcoming_anniversaries?.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {ext.upcoming_anniversaries.slice(0, 8).map(b => (
                    <li key={b.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`anniversary-row-${b.id}`}>
                      <span className="w-9 h-9 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center"><PartyPopper size={13} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold truncate">
                          {b.name}
                          {b.years_completing > 0 && (
                            <span className="ml-2 text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm align-middle">
                              {b.years_completing} {b.years_completing === 1 ? 'Year' : 'Years'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{b.designation || b.emp_id}</div>
                      </div>
                      <span className="text-xs font-display font-bold text-amber-600">{b.days_until === 0 ? 'Today' : b.days_until === 1 ? 'Tomorrow' : `In ${b.days_until}d`}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 italic py-4">No anniversaries in the next 7 days.</p>
              )
            ) : (
              ext.upcoming_birthdays?.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {ext.upcoming_birthdays.slice(0, 8).map(b => (
                    <li key={b.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`birthday-row-${b.id}`}>
                      <span className="w-9 h-9 rounded-sm bg-pink-50 text-pink-600 flex items-center justify-center"><Cake size={13} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold truncate">{b.name}</div>
                        <div className="text-xs text-slate-500">{b.designation || b.emp_id}</div>
                      </div>
                      <span className="text-xs font-display font-bold text-pink-600">{b.days_until === 0 ? 'Today' : b.days_until === 1 ? 'Tomorrow' : `In ${b.days_until}d`}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 italic py-4">No birthdays in the next 7 days.</p>
              )
            )}
          </CardContent>
        </Card>
      )}

      {/* Latest payroll banner */}
      {data.latest_payroll && (
        <Card className="border-l-4 border-l-[#1F7A6E]">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">Latest Payroll Run</div>
              <div className="text-base font-semibold mt-0.5">
                {data.latest_payroll.month} · {data.latest_payroll.employees} employees
              </div>
            </div>
            <div className="flex gap-6">
              <Stat label="Net Disbursed" value={formatINR(data.latest_payroll.total_net)} accent="text-emerald-600" />
              <Stat label="PF Contribution" value={formatINR(data.latest_payroll.total_pf)} accent="text-slate-700" />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
              <Building2 size={14} /> Department Split
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SplitBars items={data.department_split} colorClass="bg-[#1F7A6E]" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
              <Briefcase size={14} /> Project Allocation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SplitBars items={data.project_split} colorClass="bg-emerald-500" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
            <Cake size={14} /> Upcoming Birthdays (next 30 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.upcoming_birthdays.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">None upcoming.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.upcoming_birthdays.map((b) => (
                <div key={b.emp_id} className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2">
                  <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center text-[#1F7A6E] shrink-0">
                    <Cake size={16} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-medium">{b.name}</div>
                    <div className="text-xs text-slate-500">
                      {b.department} · in {b.days} day{b.days === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FaceEnrolmentCard({ fe }) {
  const [showModal, setShowModal] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('enrolled');
  const pct = fe.coverage_pct || 0;
  const tone = pct >= 80 ? 'emerald' : pct >= 50 ? 'amber' : 'rose';
  const barCls = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500' }[tone];
  const txtCls = { emerald: 'text-emerald-600', amber: 'text-amber-600', rose: 'text-rose-600' }[tone];
  return (
    <>
      <Card data-testid="face-enrolment-card" onClick={() => setShowModal(true)} className="cursor-pointer hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
            <ScanFace size={14} /> Face Enrolment Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className={`text-4xl font-display font-extrabold tracking-tight ${txtCls}`} data-testid="face-coverage-pct">
                {pct}%
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                <span className="font-semibold text-slate-700" data-testid="face-enrolled-count">{fe.enrolled}</span> of {fe.total} active staff enrolled
                {fe.pending > 0 && <> · <span className="font-semibold text-rose-600">{fe.pending}</span> pending</>}
              </div>
            </div>
          </div>
          <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden" data-testid="face-coverage-bar">
            <div className={`h-full ${barCls} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          {fe.pending_by_department?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
                Pending capture by department
              </div>
              <div className="flex flex-wrap gap-2" data-testid="face-pending-by-dept">
                {fe.pending_by_department.map((d) => (
                  <span
                    key={d.label}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs"
                    data-testid={`face-pending-dept-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <span className="text-slate-600">{d.label}</span>
                    <span className="font-display font-bold text-rose-600">{d.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 text-[11px] text-slate-400 text-right">Click to view staff lists →</div>
        </CardContent>
      </Card>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowModal(false)}
          data-testid="face-enrolment-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="font-display font-bold text-sm uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <ScanFace size={15} /> Face Enrolment — Staff Lists
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none w-7 h-7 flex items-center justify-center"
                data-testid="face-modal-close"
              >
                ×
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setActiveTab('enrolled')}
                data-testid="face-tab-enrolled"
                className={`flex-1 py-2.5 text-xs font-display font-bold uppercase tracking-wider ${activeTab === 'enrolled' ? 'border-b-2 border-[#1F7A6E] text-[#1F7A6E]' : 'text-slate-400'}`}
              >
                Enrolled ({(fe.enrolled_names || []).length || fe.enrolled})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                data-testid="face-tab-pending"
                className={`flex-1 py-2.5 text-xs font-display font-bold uppercase tracking-wider ${activeTab === 'pending' ? 'border-b-2 border-rose-500 text-rose-600' : 'text-slate-400'}`}
              >
                Pending ({(fe.pending_names || []).length || fe.pending})
              </button>
            </div>

            {/* Scrollable list */}
            <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
              {activeTab === 'enrolled' && (
                <div className="p-4 space-y-1" data-testid="face-enrolled-list">
                  {(fe.enrolled_names || []).length === 0
                    ? <p className="text-center text-sm text-slate-400 py-6">No enrolled staff</p>
                    : (fe.enrolled_names || []).map((name, i) => (
                      <div key={i} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-slate-50">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-emerald-700">{name[0]?.toUpperCase()}</span>
                        </div>
                        <span className="text-sm text-slate-700">{name}</span>
                      </div>
                    ))
                  }
                </div>
              )}
              {activeTab === 'pending' && (
                <div className="p-4 space-y-1" data-testid="face-pending-list">
                  {(() => {
                    const pendingList = (fe.pending_employees && fe.pending_employees.length)
                      ? fe.pending_employees
                      : (fe.pending_names || []).map((n) => ({ id: null, name: n, email: '' }));
                    if (pendingList.length === 0) {
                      return <p className="text-center text-sm text-emerald-600 py-6 font-semibold">All staff enrolled!</p>;
                    }
                    return pendingList.map((emp, i) => (
                      <PendingEmployeeRow
                        key={emp.id || `${emp.name}-${i}`}
                        emp={emp}
                      />
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PendingEmployeeRow({ emp }) {
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  const canRemind = Boolean(emp.id && emp.email);

  const handleRemind = async () => {
    if (!emp.id) return;
    setSending(true);
    try {
      const res = await apiPost(`/hr/face-enrolment/remind/${emp.id}`);
      setSentAt(res?.sent_at || new Date().toISOString());
      toast.success(`Reminder sent to ${emp.name}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send reminder';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-slate-50"
      data-testid={`face-pending-row-${emp.id || emp.name}`}
    >
      <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
        <span className="text-[10px] font-bold text-rose-600">{(emp.name || '?')[0]?.toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-700 truncate">{emp.name}</div>
        {emp.department && (
          <div className="text-[10px] text-slate-400 truncate">{emp.department}</div>
        )}
      </div>
      {canRemind ? (
        sentAt ? (
          <span
            className="text-[10px] font-display font-bold uppercase tracking-wider text-emerald-600 px-2"
            data-testid={`face-remind-sent-${emp.id}`}
          >
            Sent
          </span>
        ) : (
          <button
            type="button"
            onClick={handleRemind}
            disabled={sending}
            data-testid={`face-remind-btn-${emp.id}`}
            className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Send enrolment reminder to ${emp.email}`}
          >
            <Send size={10} /> {sending ? 'Sending…' : 'Remind'}
          </button>
        )
      ) : (
        <span
          className="text-[10px] text-slate-300 px-2"
          title={emp.id ? 'No email on file' : ''}
          data-testid={`face-remind-disabled-${emp.id || emp.name}`}
        >
          {emp.id ? 'No email' : ''}
        </span>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, tone, testId }) {  const toneCls = tone === 'amber' ? 'border-amber-300 bg-amber-50' : '';
  return (
    <Card className={toneCls} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</span>
          <Icon size={16} className="text-slate-400" />
        </div>
        <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">{label}</div>
      <div className={`text-xl font-bold ${accent || ''}`}>{value}</div>
    </div>
  );
}

function SplitBars({ items, colorClass }) {
  const max = Math.max(...items.map(i => i.count), 1);
  return (
    <div className="space-y-2.5">
      {items.map(item => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-sm text-slate-700 w-32 truncate" title={item.label}>{item.label}</span>
          <div className="flex-1 h-2 bg-slate-100 rounded-sm overflow-hidden">
            <div className={`h-full ${colorClass}`} style={{ width: `${(item.count / max) * 100}%` }} />
          </div>
          <span className="text-sm font-semibold w-8 text-right">{item.count}</span>
        </div>
      ))}
    </div>
  );
}
